import React, { Component } from "react";
import { connect } from "dva";
import { StoreState } from "@src/interfaces";
import { Button, Typography, Spin, message, Alert } from "antd";
import { helper } from "@src/controls/controlHelper";
import { FileOutlined, EyeOutlined, DownloadOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;

const PAGE_ID = 323;

interface CertificateViewCtrlProps {
  query?: any;
  authUser?: any;
}

interface CertificateViewCtrlState {
  loading: boolean;
  pdfUrl: string | null;
  error: string | null;
  pdfLoadError: boolean;
}

class CertificateViewCtrl extends Component<
  CertificateViewCtrlProps,
  CertificateViewCtrlState
> {
  private iframeRef: React.RefObject<HTMLIFrameElement>;

  constructor(props: CertificateViewCtrlProps) {
    super(props);
    this.state = {
      loading: false,
      pdfUrl: null,
      error: null,
      pdfLoadError: false,
    };
    this.iframeRef = React.createRef();
  }

  componentDidMount() {
    this.fetchData();
  }

  fetchData = async () => {
    try {
      this.setState({ loading: true, error: null, pdfLoadError: false });
      
      const pageInfo = await helper.getPage(PAGE_ID);
      const response: any = await helper.callPageApi(pageInfo, "find_url", {
        queryInput: JSON.stringify({
            id: this.props.query?.id
        })
      });

      if (response?.data?.data) {
        // Lấy PDF đầu tiên có certificate
        const firstProductWithCertificate = response.data.data.find(
          (item: any) => item.certificate && item.certificate.trim() !== ""
        );
        
        if (firstProductWithCertificate) {
          console.log("PDF URL:", firstProductWithCertificate.certificate);
          this.setState({ 
            pdfUrl: firstProductWithCertificate.certificate,
            loading: false 
          });
        } else {
          this.setState({ 
            pdfUrl: null,
            loading: false,
            error: "Không tìm thấy certificate nào" 
          });
        }
      } else {
        this.setState({ 
          pdfUrl: null,
          loading: false,
          error: "Không có dữ liệu" 
        });
      }
    } catch (error: any) {
      console.error("Error fetching data:", error);
      this.setState({ 
        loading: false, 
        error: error.message || "Có lỗi xảy ra khi tải dữ liệu"
      });
      message.error("Không thể tải dữ liệu certificate");
    }
  };

  handleIframeLoad = () => {
    console.log("PDF loaded successfully");
  };

  handleIframeError = () => {
    console.log("PDF load error");
    this.setState({ pdfLoadError: true });
  };

  // Hàm mở PDF trong tab mới
  openPdfInNewTab = () => {
    if (this.state.pdfUrl) {
      window.open(this.state.pdfUrl, '_blank');
    }
  };

  // Hàm tải PDF
  downloadPdf = () => {
    if (!this.state.pdfUrl) return;
    
    const pdfUrl = this.state.pdfUrl; // Type assertion since we checked above
    
    try {
      // Method 1: Thử với content-disposition header
      const downloadUrl = pdfUrl + '?response-content-disposition=attachment';
      
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = 'certificate.pdf';
      a.rel = 'noopener';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      // Method 2: Fallback với original URL nếu method 1 không work
      setTimeout(() => {
        const fallbackA = document.createElement('a');
        fallbackA.href = pdfUrl;
        fallbackA.download = 'certificate.pdf';
        fallbackA.rel = 'noopener';
        fallbackA.style.display = 'none';
        document.body.appendChild(fallbackA);
        fallbackA.click();
        document.body.removeChild(fallbackA);
      }, 500);
      
      // Thông báo hướng dẫn
      setTimeout(() => {
        message.info('Nếu file mở trong tab mới thay vì download, vui lòng click chuột phải và chọn "Save as..."');
      }, 2000);
    } catch (error) {
      console.error("Download error:", error);
      message.error("Không thể tải xuống file PDF");
    }
  };

  render() {
    const { loading, pdfUrl, error, pdfLoadError } = this.state;

    if (error) {
      return (
        <div style={{ textAlign: "center", padding: "50px" }}>
          <FileOutlined style={{ fontSize: "48px", color: "#ccc" }} />
          <br />
          <Text type="danger">{error}</Text>
          <br />
          <Button
            type="primary"
            onClick={this.fetchData}
            style={{ marginTop: 16 }}
          >
            Thử lại
          </Button>
        </div>
      );
    }

    if (loading) {
      return (
        <div style={{ textAlign: "center", padding: "50px" }}>
          <Spin size="large" />
          <br />
          <Text>Đang tải certificate...</Text>
        </div>
      );
    }

    if (!pdfUrl) {
      return (
        <div style={{ textAlign: "center", padding: "50px" }}>
          <FileOutlined style={{ fontSize: "48px", color: "#ccc" }} />
          <br />
          <Text type="secondary">Không có certificate để hiển thị</Text>
        </div>
      );
    }

    return (
      <div style={{ height: "100vh", width: "100%" }}>
        <div style={{ 
          padding: "12px 16px", 
          borderBottom: "1px solid #f0f0f0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}>
          <Title level={4} style={{ margin: 0 }}>
            <FileOutlined /> Certificate PDF
          </Title>
          <div style={{ display: "flex", gap: "8px" }}>
            <Button 
              type="default"
              icon={<EyeOutlined />}
              onClick={this.openPdfInNewTab}
            >
              Xem trong tab mới
            </Button>
            <Button 
              type="primary"
              icon={<DownloadOutlined />}
              onClick={this.downloadPdf}
            >
              Tải xuống
            </Button>
          </div>
        </div>
        
        {pdfLoadError && (
          <Alert
            message="Không thể hiển thị PDF"
            description="PDF có thể không được hỗ trợ hiển thị trực tiếp. Vui lòng sử dụng nút 'Xem trong tab mới' hoặc 'Tải xuống'."
            type="warning"
            showIcon
            style={{ margin: "16px" }}
            action={
              <Button size="small" onClick={this.openPdfInNewTab}>
                Xem ngay
              </Button>
            }
          />
        )}
        
        <iframe
          ref={this.iframeRef}
          src={`https://docs.google.com/viewer?url=${encodeURIComponent(pdfUrl)}&embedded=true`}
          width="100%"
          height="90%"
          style={{ border: "none" }}
          title="Certificate PDF"
          onLoad={this.handleIframeLoad}
          onError={this.handleIframeError}
          sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
        />
      </div>
    );
  }
}

const mapStateToProps = ({ auth }: StoreState) => ({
  authUser: auth.authUser,
});

export default connect(mapStateToProps)(CertificateViewCtrl);